/**
 * Tag — small coloured pill with optional leading icon.
 * Used for role badges, entry types, filter chips.
 */
import type { ReactNode } from 'react';
import { SECTIONS, type SectionKey } from '../sections';

export type TagTone = 'neutral' | 'brand' | 'pos' | 'neg' | 'warn' | 'info';

const TONES: Record<TagTone, string> = {
  neutral: 'bg-surface-raised text-ink-muted border border-border',
  brand:   'bg-brand-light text-brand border border-brand/20',
  pos:     'bg-emerald-50 text-emerald-700 border border-emerald-200',
  neg:     'bg-red-50 text-red-700 border border-red-200',
  warn:    'bg-amber-50 text-amber-700 border border-amber-200',
  info:    'bg-blue-50 text-blue-700 border border-blue-200',
};

export function Tag({
  children,
  tone = 'neutral',
  icon: Icon,
  section,
}: {
  children: ReactNode;
  tone?: TagTone;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  /** When set, overrides `tone` with the section accent (tint bg + colour). */
  section?: SectionKey;
}) {
  const sec = section ? SECTIONS[section] : null;
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption font-medium',
        sec ? 'border' : TONES[tone],
      ].join(' ')}
      style={sec ? { background: sec.tint, color: sec.color, borderColor: sec.color } : undefined}
    >
      {Icon && <Icon className="h-3 w-3" strokeWidth={2} />}
      {children}
    </span>
  );
}
